import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { Button, Badge, Alert, Spinner, Dropdown, Offcanvas, Tabs, Tab, Row, Col, Form } from "react-bootstrap";
import api from "../../api";
import { toast } from "react-toastify";
import FormFieldToolbox from "../../components/forms/FormFieldToolbox";
import FormCanvas from "../../components/forms/FormCanvas";
import FormFieldPropertiesPanel from "../../components/forms/FormFieldPropertiesPanel";
import FormPreview from "../../components/forms/FormPreview";
import ScoringConfigEditor from "../../components/forms/ScoringConfigEditor";
import FormExecutionProfilePanel from "../../components/forms/FormExecutionProfilePanel";
import FormBuilderStepNav from "../../components/forms/FormBuilderStepNav";
import FormPublishReadinessDrawer from "../../components/forms/FormPublishReadinessDrawer";
import FormTaskCreationRuleEditor from "../../components/forms/FormTaskCreationRuleEditor";
import ConfirmModal from "../../components/common/ConfirmModal";
import { toBuilderSection, SCORING_MODES } from "./formBuilderNormalizers";
import { TABS, TARGETS, classifyIssue as classifyIssueRaw, groupErrorsByTab, buildFieldErrorMap } from "./formValidationMapping";
import { usePermissions } from "../../context/PermissionContext";
import useSectionsHistory from "../../hooks/useSectionsHistory";
import useBreakpoint from "../../hooks/useBreakpoint";
import "./FormBuilderPage.css";

let localIdCounter = 0;
function nextLocalId() {
  localIdCounter -= 1;
  return localIdCounter;
}

// Dynamic Forms visibility audit, §7: this page is now also mounted at
// /<role>/forms/:formId/builder/:versionId for every role, not just
// /admin/... -- every navigation below must follow the SAME role prefix
// rather than a hardcoded /admin/ path.
function useRolePrefix() {
  const path = window.location.pathname;
  const match = path.match(/^\/([^/]+)\//);
  return match ? match[1] : "admin";
}

// Mirrors validators/formFieldValidator.js's OPTION_CAPABLE_TYPES. field_type
// can't be changed after a field is created (FormFieldEditor.jsx renders it
// as a read-only badge), but a field loaded from data created before that
// was true -- or edited outside this UI -- can still carry a stale `options`
// array for a type that no longer accepts options. Sending those through
// PUT .../options 400s with FORM_OPTION_REQUIRED ("Field type X does not
// accept options"); omitting them here lets the existing upsert-by-id logic
// in services/formFieldService.js#replaceOptions delete them as no-longer-
// present, self-healing the stale data on the next save.
const OPTION_CAPABLE_TYPES = new Set(["DROPDOWN", "MULTI_SELECT", "RADIO", "CHECKBOX", "YES_NO", "RATING", "RESULT_STATUS"]);

const TAB_LABELS = { [TABS.STRUCTURE]: "Structure", [TABS.SCORING]: "Scoring", [TABS.EXECUTION_PROFILE]: "Execution Profile" };

// Small red error-count badge on a tab title -- omitted entirely at
// count = 0 (a `<Badge>` showing "0" would read as "0 problems", which
// looks like a bug, not a reassurance).
function TabTitleWithBadge({ label, count }) {
  return (
    <span>
      {label}
      {!!count && <Badge bg="danger" className="ms-1 form-builder-tab-badge">{count}</Badge>}
    </span>
  );
}

// Legacy field_type compatibility map -- some existing draft forms (created
// before validators/formFieldValidator.js's canonical FIELD_TYPES registry
// settled on PROJECT_SELECT/PROJECT_MODULE_SELECT) still have rows stored
// with pre-canonical type names. The backend validator only knows the
// canonical names and rejects these outright at publish time
// ("field_type \"PROJECT_DROPDOWN\" is not supported"). This is a pure
// rename applied when the builder loads the field into memory -- id,
// field_key, label, is_required, display_order, scoring settings and
// section membership all pass through untouched; nothing is deleted,
// recreated, or duplicated. The rename only reaches the database once the
// admin clicks Save Structure, which PUTs this same field row (by its
// existing id) through the normal fields API -- an UPDATE, not an insert.
// (LEGACY_FIELD_TYPE_MAP / toBuilderSection now live in
// formBuilderNormalizers.js, imported above, so they're unit-testable
// without pulling in react-router-dom.)

// Phase 3A/Part B — the drag-and-drop dynamic form builder. All builder
// state is held here (single source of truth) and passed down to
// FormFieldToolbox (left) / FormCanvas (center) / FormFieldPropertiesPanel
// (right) / FormPreview / ScoringConfigEditor. Saving is a 3-step sequence
// (sections -> fields -> options) because later steps
// comments for the exact position-based id-remapping strategy used.
export default function FormBuilderPage() {
  const { formId, versionId } = useParams();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(null);
  const [sections, setSections] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});
  // Part B save-behaviour requirement: surface "Unsaved changes" after any
  // structural edit (add/delete/move field, move section, property change,
  // option reorder) rather than saving on every mutation -- saveAll() is
  // still the only thing that ever calls the PUT .../sections|fields|options
  // APIs. Reset on a fresh load() and after a successful saveAll().
  const [dirty, setDirty] = useState(false);
  // `?tab=` is an additive fallback for callers that can't attach router
  // navigation state (e.g. the Forms Workspace Build tab's card actions,
  // which use a plain `window.location.assign` full navigation) -- router
  // state from an in-app <Link>/navigate() still takes priority.
  const [activeTab, setActiveTab] = useState(
    location.state?.initialTab
      || (location.state?.validationErrors ? "preview" : null)
      || new URLSearchParams(location.search).get("tab")
      || "structure"
  );
  // If we were routed here straight from a failed Publish click on the
  // Forms list page (see FormManagementPage.jsx's publishDraft()), that
  // response's exact validation errors ride along in navigation state so
  // they render immediately -- the user shouldn't have to click "Check
  // Publish Readiness" again just to see what the list page's toast
  // already told them existed.
  const [validation, setValidation] = useState(
    location.state?.validationErrors
      ? { is_publishable: false, errors: location.state.validationErrors }
      : null
  );
  const [scoring, setScoring] = useState(null);
  const [executionProfile, setExecutionProfile] = useState(null);
  const [profileValidation, setProfileValidation] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  // "Edit Current Version" -- null while unknown/not applicable (e.g. a
  // Draft version, or not yet fetched). Only ever meaningful for a
  // PUBLISHED version; see the reopen-eligibility GET below.
  const [reopenEligibility, setReopenEligibility] = useState(null);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [reopening, setReopening] = useState(false);
  // Field-delete confirmation -- {sectionIndex, fieldIndex} of the field
  // pending removal, or null when no confirmation is showing. Deletion
  // itself stays a pure local-state edit (same as every other structural
  // change here) until Save Structure; see confirmRemoveField below.
  const [pendingFieldDelete, setPendingFieldDelete] = useState(null);
  const gradeConfigRef = useRef(null);

  const { can } = usePermissions();
  const rolePrefix = useRolePrefix();
  const canPublish = can("form_versions", "publish");
  const canReopen = can("form_versions", "reopen");

  const readOnly = version && version.status !== "DRAFT";
  const reopenEligible = !!reopenEligibility?.eligible;

  const loadReopenEligibility = async (versionStatus) => {
    if (versionStatus !== "PUBLISHED") {
      setReopenEligibility(null);
      return;
    }
    try {
      const { data } = await api.get(`/forms/${formId}/versions/${versionId}/reopen-eligibility`);
      setReopenEligibility(data?.data || null);
    } catch (err) {
      console.error("Load reopen eligibility error:", err);
      setReopenEligibility(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/forms/${formId}/versions/${versionId}/builder`);
      const structure = data?.data;
      setVersion(structure?.version || null);
      await loadReopenEligibility(structure?.version?.status);

      const legacyRenames = [];
      const legacyScoringModes = [];
      setSections((structure?.sections || []).map((s) => toBuilderSection(
        s,
        (fieldKey, from, to) => legacyRenames.push({ fieldKey, from, to }),
        (fieldKey, from, to) => legacyScoringModes.push({ fieldKey, from, to })
      )));
      if (legacyRenames.length) {
        toast.warning(
          `${legacyRenames.length} field(s) used a legacy, unsupported field_type and were normalized in the editor ` +
          `(${legacyRenames.map((r) => `${r.fieldKey}: ${r.from} → ${r.to}`).join(", ")}) — click Save Structure to persist the fix.`
        );
      }
      if (legacyScoringModes.length) {
        toast.warning(
          `${legacyScoringModes.length} field(s) used a legacy, unsupported scoring_mode and were normalized in the editor ` +
          `(${legacyScoringModes.map((r) => `${r.fieldKey}: "${r.from}" → ${r.to}`).join(", ")}) — click Save Structure to persist the fix.`
        );
      }

      const rawGradeConfig = structure?.version?.grade_config_json;
      let parsedGradeConfig = typeof rawGradeConfig === "string"
        ? (rawGradeConfig ? JSON.parse(rawGradeConfig) : [])
        : (rawGradeConfig || []);
      // The backend's publish-time validator (services/formGradeService.js)
      // requires grade_config_json to be an ARRAY of grade bands; the
      // save-time validator only checks "well-formed JSON", so a
      // differently-shaped object (e.g. { rating_labels, score_categories })
      // can end up stored here without ever going through this editor.
      // Loading that shape as-is would both crash "+ Add Band" (spreading a
      // non-array) and misrepresent what's actually configured — treat
      // anything that isn't already an array as "no bands configured",
      // which the same publish-time validator accepts (null/[] both pass).
      if (!Array.isArray(parsedGradeConfig)) {
        console.warn(
          "grade_config_json was not an array of grade bands (got:",
          parsedGradeConfig,
          ") — resetting to an empty band list in the editor. Click Save Scoring to persist the fix."
        );
        toast.warning("This version's grade configuration was in an invalid format and has been reset to \"No Grade Bands\" in the Scoring tab — click Save Scoring to persist that fix.");
        parsedGradeConfig = [];
      }
      setScoring({
        scoring_enabled: !!structure?.version?.scoring_enabled,
        scoring_method: structure?.version?.scoring_method || "NONE",
        total_max_score: structure?.version?.total_max_score ?? "",
        pass_percentage: structure?.version?.pass_percentage ?? "",
        grade_config_json: parsedGradeConfig,
      });
      setDirty(legacyRenames.length > 0 || legacyScoringModes.length > 0);
      setSelectedFieldIndex(null);
    } catch (err) {
      console.error("Load form builder error:", err);
      toast.error(err.response?.data?.message || "Failed to load form builder");
    } finally {
      setLoading(false);
    }
  };

  const loadExecutionProfile = async () => {
    try {
      const { data } = await api.get(`/forms/${formId}/versions/${versionId}/execution-profile`);
      setExecutionProfile(data?.data || null);
    } catch (_err) {
      // 404 = not configured yet — that is a normal, expected state, not an error.
      setExecutionProfile(null);
    }
  };

  useEffect(() => {
    load();
    loadExecutionProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, versionId]);

  // Every repeatable section's own fields — the only sections/fields the
  // Execution Profile tab's dropdowns offer, per §6 ("primary repeatable
  // section" + its own RESULT_STATUS/TEST_CASE_ID/etc. fields).
  const repeatableSections = useMemo(() => sections.filter((s) => s.is_repeatable && s.id > 0), [sections]);
  const primarySectionFields = useMemo(() => {
    const section = repeatableSections.find((s) => s.id === executionProfile?.primary_repeatable_section_id);
    return (section?.fields || []).filter((f) => f.id > 0);
  }, [repeatableSections, executionProfile]);

  const allFieldKeys = useMemo(
    () => sections.flatMap((s) => (s.fields || []).map((f) => f.field_key)).filter(Boolean),
    [sections]
  );

  // Full field objects (not just keys) so FormFieldEditor can check, before
  // deleting an option, whether some OTHER field's conditional_visibility_json
  // references this field's option by value (see §7 of the Remove Option
  // feature) -- conditions reference an option by its raw option_code
  // string (matching DynamicFormRenderer.jsx's actual submitted value), not
  // by a stable option id, so this has to be computed from the live field
  // list rather than a DB foreign key.
  const allFields = useMemo(
    () => sections.flatMap((s) => (s.fields || []).map((f) => ({ field_key: f.field_key, label: f.label, conditional_visibility_json: f.conditional_visibility_json }))),
    [sections]
  );

  // Publish-readiness issues (see services/formVersionService.js's
  // validatePublishReadiness()) carry a field_key and/or an error code but
  // never a tab name, since the backend has no concept of the builder's UI
  // -- classifyIssue/groupErrorsByTab/buildFieldErrorMap (all in
  // formValidationMapping.js, the single central mapping) do that
  // resolution; this file only calls into them, never re-derives its own.
  const classifyIssue = (error) => classifyIssueRaw(error, sections);

  const errorsByTab = useMemo(() => groupErrorsByTab(validation?.errors, sections), [validation, sections]);
  const fieldErrorMap = useMemo(() => buildFieldErrorMap(validation?.errors, sections), [validation, sections]);
  const gradeConfigError = useMemo(
    () => (validation?.errors || []).find((e) => classifyIssueRaw(e, sections).target === TARGETS.GRADE_CONFIGURATION) || null,
    [validation, sections]
  );
  const selectedFieldErrors = selectedFieldIndex != null
    ? fieldErrorMap[sections[selectedIndex]?.fields?.[selectedFieldIndex]?.field_key] || []
    : [];

  const jumpToIssue = (error) => {
    const { tab, sectionIndex, fieldIndex } = classifyIssue(error);
    if (sectionIndex != null) {
      setSelectedIndex(sectionIndex);
      setSelectedFieldIndex(fieldIndex != null ? fieldIndex : null);
    }
    setActiveTab(tab);
    if (tab === TABS.SCORING) {
      // Scroll/focus the Grade Configuration control the same tick the tab
      // switches -- the ref only attaches once ScoringConfigEditor mounts,
      // so this has to run after React commits the new activeTab.
      requestAnimationFrame(() => gradeConfigRef.current?.focusGradeBands?.());
    }
  };

  // Removes every currently-tracked error that the given predicate says is
  // now resolved -- used right after a successful save to make the tab
  // badge/inline field state/top panel drop that issue immediately,
  // without waiting for the admin to click "Check Publish Readiness"
  // again. Never marks something resolved on a guess: each call site below
  // only clears errors a successful save has just proven fixed server-side.
  const clearResolvedIssues = (isResolved) => {
    setValidation((prev) => {
      if (!prev?.errors?.length) return prev;
      const remaining = prev.errors.filter((e) => !isResolved(e, classifyIssue(e)));
      if (remaining.length === prev.errors.length) return prev;
      return remaining.length ? { ...prev, errors: remaining } : { is_publishable: true, errors: [] };
    });
  };

  const makeNewField = (fieldType) => ({
    id: nextLocalId(),
    field_key: `field_${Math.abs(nextLocalId())}`,
    field_type: fieldType,
    label: fieldType.replace(/_/g, " "),
    description: "", placeholder: "", help_text: "", default_value: "",
    is_required: false, is_read_only: false, display_order: 0,
    minimum_value: null, maximum_value: null, minimum_length: null, maximum_length: null,
    validation_pattern: "", validation_message: "",
    score_enabled: false, maximum_score: null, weightage: null,
    scoring_mode: "NONE", scoring_aggregation: "CAPPED_SUM", exclude_not_applicable_from_score: true,
    minimum_passing_score: null, score_precision: 2, allow_manual_score: false, manual_score_required: false,
    include_in_response_table: true, include_in_export: true, include_in_print: true,
    conditional_visibility_json: null, calculation_config_json: null,
    options: [],
  });

  const addSection = () => {
    setSections((prev) => [...prev, { id: nextLocalId(), title: "New Section", description: "", display_order: prev.length, is_repeatable: false, minimum_repetitions: null, maximum_repetitions: null, score_enabled: false, weightage: null, minimum_passing_score: null, fields: [] }]);
    setSelectedIndex(sections.length);
    setSelectedFieldIndex(null);
    setDirty(true);
  };

  const duplicateSection = (index) => {
    setSections((prev) => {
      const source = prev[index];
      if (!source) return prev;
      const copy = {
        ...source,
        id: nextLocalId(),
        title: `${source.title || "Section"} (copy)`,
        fields: (source.fields || []).map((f) => ({ ...f, id: nextLocalId(), options: (f.options || []).map((o) => ({ ...o, id: nextLocalId() })) })),
      };
      const next = [...prev];
      next.splice(index + 1, 0, copy);
      return next;
    });
    setDirty(true);
  };

  // Drag-and-drop reorder — moves the section at fromIndex to sit at
  // toIndex. Native HTML5 DnD only (no new library), wired up from
  // FormCanvas's onReorderSections.
  const reorderSection = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    setSections((prev) => {
      if (fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setSelectedIndex((prev) => {
      if (prev === fromIndex) return toIndex;
      if (fromIndex < prev && toIndex >= prev) return prev - 1;
      if (fromIndex > prev && toIndex <= prev) return prev + 1;
      return prev;
    });
    setDirty(true);
  };

  const removeSection = (index) => {
    if (!window.confirm("Remove this section? Fields inside it must be empty or this will be rejected on save.")) return;
    setSections((prev) => prev.filter((_, i) => i !== index));
    setSelectedIndex(0);
    setSelectedFieldIndex(null);
    setDirty(true);
  };

  const changeSection = (updated) => {
    setSections((prev) => prev.map((s, i) => (i === selectedIndex ? updated : s)));
    setDirty(true);
  };

  const toggleCollapse = (sectionId) => {
    setCollapsedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const selectSection = (sectionIndex) => {
    setSelectedIndex(sectionIndex);
    setSelectedFieldIndex(null);
  };

  const selectField = (sectionIndex, fieldIndex) => {
    setSelectedIndex(sectionIndex);
    setSelectedFieldIndex(fieldIndex);
  };

  // Toolbox "click to add" -- always targets the currently selected
  // section (existing behavior, kept alongside the new drag-to-a-specific-
  // section-and-position path below).
  const addField = (fieldType) => {
    const newField = makeNewField(fieldType);
    setSections((prev) => prev.map((s, i) => (i === selectedIndex ? { ...s, fields: [...(s.fields || []), newField] } : s)));
    setDirty(true);
  };

  // Toolbox drag-and-drop -- inserts at the exact drop position within the
  // target section (which may not be the currently "selected" one).
  const addFieldAt = (sectionIndex, fieldType, atIndex) => {
    const newField = makeNewField(fieldType);
    setSections((prev) => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      const fields = [...(s.fields || [])];
      fields.splice(Math.min(atIndex, fields.length), 0, newField);
      return { ...s, fields };
    }));
    setSelectedIndex(sectionIndex);
    setSelectedFieldIndex(atIndex);
    setDirty(true);
  };

  const changeField = (updated) => {
    setSections((prev) => prev.map((s, i) => {
      if (i !== selectedIndex || selectedFieldIndex == null) return s;
      const fields = [...s.fields];
      fields[selectedFieldIndex] = updated;
      return { ...s, fields };
    }));
    setDirty(true);
  };

  const duplicateField = (sectionIndex, fieldIndex) => {
    setSections((prev) => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      const source = s.fields[fieldIndex];
      const copy = { ...source, id: nextLocalId(), field_key: `${source.field_key}_copy`, options: (source.options || []).map((o) => ({ ...o, id: nextLocalId() })) };
      const fields = [...s.fields];
      fields.splice(fieldIndex + 1, 0, copy);
      return { ...s, fields };
    }));
    setDirty(true);
  };

  // Opens the confirmation modal instead of deleting immediately -- the
  // actual removal (and its "this also removes N options" warning) happens
  // in confirmRemoveField once the admin confirms.
  const removeField = (sectionIndex, fieldIndex) => {
    setPendingFieldDelete({ sectionIndex, fieldIndex });
  };

  const pendingDeleteField = pendingFieldDelete
    ? sections[pendingFieldDelete.sectionIndex]?.fields?.[pendingFieldDelete.fieldIndex]
    : null;
  const pendingDeleteOptionCount = (pendingDeleteField?.options || []).length;

  const confirmRemoveField = () => {
    if (!pendingFieldDelete) return;
    const { sectionIndex, fieldIndex } = pendingFieldDelete;
    setSections((prev) => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      return { ...s, fields: s.fields.filter((_, fi) => fi !== fieldIndex) };
    }));
    if (selectedIndex === sectionIndex && selectedFieldIndex === fieldIndex) setSelectedFieldIndex(null);
    setDirty(true);
    setPendingFieldDelete(null);
  };

  const cancelRemoveField = () => setPendingFieldDelete(null);

  // Drag-and-drop field move — handles reordering within one section AND
  // moving a field into a different section, in one step (FormCanvas
  // can't tell these apart at the DOM level; both are "drop at this
  // section+index").
  const moveFieldAcross = (fromSectionIndex, fromFieldIndex, toSectionIndex, toFieldIndex) => {
    setSections((prev) => {
      const next = prev.map((s) => ({ ...s, fields: [...(s.fields || [])] }));
      const [moved] = next[fromSectionIndex].fields.splice(fromFieldIndex, 1);
      if (!moved) return prev;
      let insertAt = toFieldIndex;
      if (fromSectionIndex === toSectionIndex && fromFieldIndex < toFieldIndex) insertAt -= 1;
      next[toSectionIndex].fields.splice(Math.max(insertAt, 0), 0, moved);
      return next;
    });
    setSelectedIndex(toSectionIndex);
    setSelectedFieldIndex(null);
    setDirty(true);
  };

  // The 3-step save sequence — see file header comment for why position-
  // based id remapping (not a returned correlation id) is used: both the
  // request and the response are sorted by a unique, sequential
  // display_order we assign here, so array position lines up 1:1.
  // Client-side safety net before Save Structure -- the Scoring Mode
  // dropdown and the Score Enabled checkbox's own onChange (see
  // FormFieldEditor.jsx) both already keep scoring_mode canonical, so this
  // should never actually trip in normal use. It exists so a malformed
  // value (e.g. from data loaded before this fix, or restored from
  // somewhere that bypassed the editor) is caught here -- with the exact
  // field selected so it's visible -- instead of being sent to the backend
  // and only discovered as a generic 400.
  const findInvalidScoringModeField = () => {
    for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex += 1) {
      const fields = sections[sectionIndex].fields || [];
      for (let fieldIndex = 0; fieldIndex < fields.length; fieldIndex += 1) {
        const f = fields[fieldIndex];
        if (!SCORING_MODES.includes(f.scoring_mode || "NONE")) {
          return { sectionIndex, fieldIndex, field: f };
        }
      }
    }
    return null;
  };

  const saveAll = async () => {
    const invalid = findInvalidScoringModeField();
    if (invalid) {
      setSelectedIndex(invalid.sectionIndex);
      setSelectedFieldIndex(invalid.fieldIndex);
      toast.error(`"${invalid.field.label || invalid.field.field_key}" has an unsupported scoring mode ("${invalid.field.scoring_mode}"). Fix it in the Properties panel before saving.`);
      return;
    }
    setSaving(true);
    // Tracks which of the three sequential PUT calls below is in flight, so
    // the catch block can report exactly which step/URL/payload the backend
    // rejected instead of a single generic "save failed" for all three.
    let currentStep = "sections";
    let currentUrl = `/forms/${formId}/versions/${versionId}/sections`;
    let currentPayload = null;
    try {
      const sectionsPayload = sections.map((s, idx) => ({
        id: s.id > 0 ? s.id : undefined,
        title: s.title,
        description: s.description,
        display_order: idx,
        is_repeatable: s.is_repeatable,
        minimum_repetitions: s.minimum_repetitions,
        maximum_repetitions: s.maximum_repetitions,
        score_enabled: s.score_enabled,
        weightage: s.weightage,
        minimum_passing_score: s.minimum_passing_score,
        is_active: true,
      }));
      currentPayload = { sections: sectionsPayload };
      const { data: sectionsRes } = await api.put(currentUrl, currentPayload);
      const savedSections = sectionsRes.data;

      const sectionsWithRealIds = sections.map((s, idx) => ({ ...s, id: savedSections[idx].id }));

      const flatFields = [];
      sectionsWithRealIds.forEach((s) => {
        (s.fields || []).forEach((f) => flatFields.push({ ...f, form_section_id: s.id }));
      });
      const fieldsPayload = flatFields.map((f, idx) => ({
        id: f.id > 0 ? f.id : undefined,
        form_section_id: f.form_section_id,
        field_key: f.field_key,
        field_type: f.field_type,
        label: f.label,
        description: f.description,
        placeholder: f.placeholder,
        help_text: f.help_text,
        default_value: f.default_value,
        is_required: f.is_required,
        is_read_only: f.is_read_only,
        display_order: idx,
        minimum_value: f.minimum_value,
        maximum_value: f.maximum_value,
        minimum_length: f.minimum_length,
        maximum_length: f.maximum_length,
        validation_pattern: f.validation_pattern,
        validation_message: f.validation_message,
        score_enabled: f.score_enabled,
        maximum_score: f.maximum_score,
        weightage: f.weightage,
        scoring_mode: f.scoring_mode,
        scoring_aggregation: f.scoring_aggregation,
        exclude_not_applicable_from_score: f.exclude_not_applicable_from_score,
        minimum_passing_score: f.minimum_passing_score,
        score_precision: f.score_precision,
        allow_manual_score: f.allow_manual_score,
        manual_score_required: f.manual_score_required,
        include_in_response_table: f.include_in_response_table,
        include_in_export: f.include_in_export,
        include_in_print: f.include_in_print,
        conditional_visibility_json: f.conditional_visibility_json,
        calculation_config_json: f.calculation_config_json,
      }));
      currentStep = "fields";
      currentUrl = `/forms/${formId}/versions/${versionId}/fields`;
      currentPayload = { fields: fieldsPayload };
      const { data: fieldsRes } = await api.put(currentUrl, currentPayload);
      const savedFields = fieldsRes.data;

      const flatOptions = [];
      let droppedStaleOptions = 0;
      flatFields.forEach((f, fIdx) => {
        const realFieldId = savedFields[fIdx].id;
        if (!OPTION_CAPABLE_TYPES.has(f.field_type)) {
          droppedStaleOptions += (f.options || []).length;
          return;
        }
        (f.options || []).forEach((o) => flatOptions.push({ ...o, form_field_id: realFieldId }));
      });
      if (droppedStaleOptions > 0) {
        console.warn(`Dropped ${droppedStaleOptions} stale option(s) attached to field(s) whose type no longer accepts options.`);
      }
      const optionsPayload = flatOptions.map((o, idx) => ({
        id: o.id > 0 ? o.id : undefined,
        form_field_id: o.form_field_id,
        option_code: o.option_code,
        option_label: o.option_label,
        option_value: o.option_value,
        score_value: o.score_value,
        // Task spec §14: the canonical employee identity a subject-field
        // option represents -- backend-validated against a real, active
        // account (services/formFieldService.js#replaceOptions). null for
        // every ordinary (non-employee) option, unchanged.
        user_id: o.user_id ?? null,
        is_not_applicable: o.is_not_applicable,
        is_default: o.is_default,
        display_order: idx,
      }));
      currentStep = "options";
      currentUrl = `/forms/${formId}/versions/${versionId}/options`;
      currentPayload = { options: optionsPayload };
      await api.put(currentUrl, currentPayload);

      // A successful save means the backend's own field validator
      // (validators/formFieldValidator.js) accepted every field_type and
      // scoring_mode in this payload -- so any previously-tracked issue of
      // those two kinds is now provably resolved, without waiting for the
      // admin to click Check Publish Readiness again. GENERIC covers the
      // save-time structural conflicts thrown by services/formFieldService.js
      // (e.g. the old "still has options" guard, or the field-in-use/
      // has-submission-data guards) -- a successful save of this exact same
      // fields+options payload proves that conflict no longer exists.
      // Weightage-sum-to-100 is deliberately NOT cleared here: that rule is
      // only enforced at publish-readiness time, not by this save endpoint.
      clearResolvedIssues((_e, classified) => classified.target === TARGETS.FIELD_TYPE || classified.target === TARGETS.SCORING_MODE || classified.target === TARGETS.GENERIC);
      toast.success("Form structure saved");
      await load();
    } catch (err) {
      const data = err.response?.data;
      // Full diagnostic detail for whoever is debugging this -- which of the
      // three PUT calls failed, the exact URL/payload sent, and the backend's
      // actual status/body, never just "Save failed".
      console.error("Save form structure error:", {
        step: currentStep,
        url: currentUrl,
        method: "PUT",
        payload: currentPayload,
        status: err.response?.status,
        data,
        message: err.message,
      });

      if (data?.code === "FORM_VERSION_IMMUTABLE") {
        // requireDraftVersion() in services/formSectionService.js -- this
        // version is Published (or otherwise non-DRAFT); the versioning
        // safeguard is intentional and must not be bypassed. Point the admin
        // at the existing "Clone as new draft" action instead of a bare 409.
        toast.error("Published version cannot be edited. Create a new version to continue editing.");
      } else if (data?.code) {
        const fieldKeyMatch = /\(([a-z0-9_]+)\)/i.exec(data.message || "");
        const syntheticError = { code: data.code, message: data.message, field_key: fieldKeyMatch ? fieldKeyMatch[1] : null };
        setValidation({ is_publishable: false, errors: [syntheticError] });
        // Show the backend's own validation message (e.g. "Field #3 (ease_of_navigation): option_label is required.")
        // rather than a generic bucket string, per the field it actually names.
        toast.error(data.message || "Structure could not be saved.");
        jumpToIssue(syntheticError);
      } else {
        toast.error(data?.message || err.message || "Failed to save form structure");
      }
    } finally {
      setSaving(false);
    }
  };

  const saveScoring = async () => {
    setSaving(true);
    try {
      const payload = {
        ...scoring,
        // Strip the local, negative React-key ids used only for the grade
        // band editor's table rows — never part of the stored config.
        grade_config_json: (scoring.grade_config_json || []).map(({ id, ...band }) => band),
      };
      await api.patch(`/forms/${formId}/versions/${versionId}/scoring`, payload);
      // The save endpoint's validator (validators/formVersionValidator.js)
      // now runs the same grade-band shape check publish-readiness does --
      // a successful save proves grade_config_json is valid, so clear that
      // issue immediately rather than waiting for a fresh readiness check.
      clearResolvedIssues((_e, classified) => classified.target === TARGETS.GRADE_CONFIGURATION);
      toast.success("Scoring configuration saved");
      await load();
    } catch (err) {
      const data = err.response?.data;
      if (data?.code) {
        const syntheticError = { code: data.code, message: data.message, field_key: null };
        setValidation({ is_publishable: false, errors: [syntheticError] });
        toast.error("Scoring could not be saved — see the issue below.");
        jumpToIssue(syntheticError);
      } else {
        toast.error(data?.message || "Failed to save scoring configuration");
      }
    } finally {
      setSaving(false);
    }
  };

  const updateProfile = (patch) => setExecutionProfile((prev) => ({ ...(prev || { require_all_cases_executed: false, include_blocked_in_pass_rate_denominator: true, exclude_na_from_denominator: true, task_creation_enabled: false }), ...patch }));

  const saveExecutionProfile = async () => {
    if (!executionProfile?.primary_repeatable_section_id || !executionProfile?.result_status_field_id) {
      toast.warning("Select a repeatable section and its result-status field first");
      return;
    }
    setSavingProfile(true);
    try {
      const { data } = await api.put(`/forms/${formId}/versions/${versionId}/execution-profile`, executionProfile);
      setExecutionProfile(data?.data);
      setProfileValidation(null);
      toast.success("Execution profile saved");
    } catch (err) {
      const details = err.response?.data?.details?.errors;
      if (details) setProfileValidation(details);
      toast.error(err.response?.data?.message || "Failed to save execution profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const runProfileValidation = async () => {
    try {
      const { data } = await api.post(`/forms/${formId}/versions/${versionId}/execution-profile/validate`, executionProfile || {});
      setProfileValidation(data?.data?.errors || []);
      if (data?.data?.valid) toast.success("Execution profile configuration is valid");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to validate execution profile");
    }
  };

  const runPreviewValidation = async () => {
    try {
      const { data } = await api.get(`/forms/${formId}/versions/${versionId}/preview`);
      const result = data?.data?.validation || null;
      setValidation(result);
      if (result && !result.is_publishable) {
        toast.warning(`${result.errors?.length || 0} validation issue(s) found — see details below.`);
      } else if (result?.is_publishable) {
        toast.success("This version is ready to publish.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to run preview validation");
    }
  };

  // A published/archived version is immutable by design (the read-only
  // toolbox/canvas below enforce that), but until now the only way to get
  // back to an editable draft was to leave the builder, go back to the
  // Forms list, open its "More" menu, choose "New Version", then re-open
  // the builder for that new version -- a multi-step detour. This clones
  // the current version (the same endpoint the list page's "New Version"
  // action already calls) and jumps straight into the new draft's own
  // builder, right from where the user already is.
  const [creatingVersion, setCreatingVersion] = useState(false);
  const createNewVersionAndEdit = async () => {
    setCreatingVersion(true);
    try {
      const { data } = await api.post(`/forms/${formId}/versions/${versionId}/clone`, {});
      const newVersionId = data?.data?.id || data?.id;
      toast.success("New draft version created — opening it now.");
      if (newVersionId) {
        window.location.assign(`/${rolePrefix}/forms/${formId}/builder/${newVersionId}`);
      } else {
        setCreatingVersion(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create a new version");
      setCreatingVersion(false);
    }
  };

  // "Edit Current Version" -- PUBLISHED -> DRAFT on this SAME version (no
  // clone, no new version_number; see services/formVersionService.js's
  // reopenToDraft()). Only ever offered when reopenEligibility.eligible is
  // true, but the backend re-checks the same "zero responses" rule itself
  // at the moment of the request regardless -- a stale eligibility read
  // (e.g. a recipient started a submission in the meantime) is rejected
  // there with FORM_VERSION_HAS_RESPONSES, handled below.
  const reopenCurrentVersion = async () => {
    setReopening(true);
    try {
      await api.post(`/forms/${formId}/versions/${versionId}/reopen`);
      setShowReopenConfirm(false);
      toast.success("Version reopened — it's a draft now, and fully editable.");
      await load();
    } catch (err) {
      const data = err.response?.data;
      setShowReopenConfirm(false);
      if (data?.code === "FORM_VERSION_HAS_RESPONSES") {
        toast.error(data.message || "This version already has responses and can no longer be edited directly. Create a new version instead.");
        setReopenEligibility({ eligible: false, reason: "HAS_RESPONSES", response_count: data.details?.response_count ?? 1 });
      } else {
        toast.error(data?.message || "Failed to reopen this version for editing");
      }
    } finally {
      setReopening(false);
    }
  };

  const publish = () => setShowPublishConfirm(true);

  const confirmPublish = async () => {
    setSaving(true);
    try {
      await api.post(`/forms/${formId}/versions/${versionId}/publish`);
      setShowPublishConfirm(false);
      toast.success("Form version published");
      await load();
    } catch (err) {
      const data = err.response?.data;
      setShowPublishConfirm(false);
      if (data?.code === "FORM_VERSION_INVALID") {
        setValidation({ is_publishable: false, errors: data.details?.errors || [] });
        toast.error("Publish blocked — see the issues panel below.");
      } else {
        toast.error(data?.message || "Failed to publish form version");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="unit-page"><div className="unit-loader"><Spinner animation="border" /><p>Loading builder...</p></div></div>;
  }

  if (!version) {
    return <div className="unit-page"><Alert variant="danger">Form version not found.</Alert></div>;
  }

  return (
    <div className="unit-page form-builder-page">

      <div className="unit-header">
        <div>
          <div className="unit-page-label">
            <Link to={`/${rolePrefix}/forms`} className="form-builder-back-link">
              <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
              Forms
            </Link>
          </div>
          <h2>Form Builder — Version {version.version_number}</h2>
          <p>
            Status: <Badge bg={version.status === "PUBLISHED" ? "success" : version.status === "ARCHIVED" ? "secondary" : "warning"} text={version.status === "DRAFT" ? "dark" : undefined}>{version.status}</Badge>
            {!readOnly && dirty && <Badge bg="warning" text="dark" className="ms-2">Unsaved changes</Badge>}
          </p>
        </div>
        <div className="unit-header-actions">
          {readOnly && canReopen && (
            <span title={!reopenEligible ? "This version has responses and can no longer be edited directly. Create a new version instead." : undefined}>
              <Button className="unit-btn unit-btn-primary" onClick={() => setShowReopenConfirm(true)} disabled={!reopenEligible || reopening}>
                <i className="fa-solid fa-pen" aria-hidden="true"></i>
                Edit Current Version
              </Button>
            </span>
          )}
          {readOnly && (
            <Button className={`unit-btn ${reopenEligible ? "unit-btn-light" : "unit-btn-primary"}`} onClick={createNewVersionAndEdit} disabled={creatingVersion}>
              <i className="fa-solid fa-code-branch" aria-hidden="true"></i>
              {creatingVersion ? "Creating..." : "Create New Version to Edit"}
            </Button>
          )}
          {!readOnly && (
            <Button className="unit-btn unit-btn-light" onClick={saveAll} disabled={saving}>
              <i className="fa-solid fa-floppy-disk" aria-hidden="true"></i>
              {saving ? "Saving..." : "Save Structure"}
            </Button>
          )}
          <Button className="unit-btn unit-btn-light" onClick={runPreviewValidation}>
            <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
            Check Publish Readiness
          </Button>
          {!readOnly && canPublish && (
            <span title={validation && !validation.is_publishable ? "Resolve publish-readiness issues first." : undefined}>
              <Button
                className="unit-btn unit-btn-primary"
                onClick={publish}
                disabled={saving || (validation != null && !validation.is_publishable)}
              >
                <i className="fa-solid fa-paper-plane" aria-hidden="true"></i>
                Publish
              </Button>
            </span>
          )}
        </div>
      </div>

      {readOnly && (
        <Alert variant="secondary" className="form-builder-readonly-banner">
          <div className="form-builder-readonly-banner-text">
            <i className="fa-solid fa-lock" aria-hidden="true"></i>
            <div>
              <strong>Published version is read-only.</strong>
              <div>
                {reopenEligible
                  ? "No responses have started for this version — you can edit it directly, or create a new version instead."
                  : "This version has responses and is locked from direct editing. Create a new version to make structural changes."}
              </div>
            </div>
          </div>
          <div className="form-builder-readonly-banner-actions">
            {canReopen && (
              <span title={!reopenEligible ? "This version has responses and can no longer be edited directly. Create a new version instead." : undefined}>
                <Button className="unit-btn unit-btn-primary" onClick={() => setShowReopenConfirm(true)} disabled={!reopenEligible || reopening}>
                  <i className="fa-solid fa-pen" aria-hidden="true"></i>
                  Edit Current Version
                </Button>
              </span>
            )}
            <Button className={`unit-btn ${reopenEligible ? "unit-btn-light" : "unit-btn-primary"}`} onClick={createNewVersionAndEdit} disabled={creatingVersion}>
              <i className="fa-solid fa-code-branch" aria-hidden="true"></i>
              {creatingVersion ? "Creating..." : "Create New Version to Edit"}
            </Button>
            <Button className="unit-btn unit-btn-light" onClick={() => setActiveTab("preview")}>
              <i className="fa-solid fa-eye" aria-hidden="true"></i>
              Preview
            </Button>
          </div>
        </Alert>
      )}

      {validation && !validation.is_publishable && !!validation.errors?.length && (
        <Alert variant="danger" className="form-builder-issues-panel">
          <div className="form-builder-issues-heading">
            {validation.errors.length} issue{validation.errors.length === 1 ? "" : "s"} prevent{validation.errors.length === 1 ? "s" : ""} publishing
          </div>
          {[TABS.STRUCTURE, TABS.SCORING, TABS.EXECUTION_PROFILE].map((tab) => {
            const tabErrors = errorsByTab[tab] || [];
            if (!tabErrors.length) return null;
            return (
              <div key={tab} className="form-builder-issues-group">
                <div className="form-builder-issues-group-label">{TAB_LABELS[tab]}</div>
                <ul className="form-builder-issues-list">
                  {tabErrors.map((error, i) => (
                    <li key={i}>
                      <button type="button" className="form-builder-issue-item" onClick={() => jumpToIssue(error)}>
                        <span className="form-builder-issue-message">
                          {error.field_key && <strong>{error.field_key}: </strong>}
                          {error.message}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </Alert>
      )}

      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
        <Tab eventKey="structure" title={<TabTitleWithBadge label="Structure" count={errorsByTab[TABS.STRUCTURE]?.length} />}>
          <div className="form-builder-3col">
            <div className="form-builder-col-toolbox">
              <FormFieldToolbox onAddField={addField} disabled={readOnly} />
            </div>
            <div className="form-builder-col-canvas">
              <FormCanvas
                sections={sections}
                selectedSectionIndex={selectedIndex}
                selectedFieldIndex={selectedFieldIndex}
                collapsedSections={collapsedSections}
                onToggleCollapse={toggleCollapse}
                onSelectField={selectField}
                onSelectSection={selectSection}
                onAddSection={addSection}
                onReorderSections={reorderSection}
                onDuplicateSection={duplicateSection}
                onRemoveSection={removeSection}
                onAddFieldAt={addFieldAt}
                onMoveField={moveFieldAcross}
                onDuplicateField={duplicateField}
                onRemoveField={removeField}
                readOnly={readOnly}
                fieldErrorMap={fieldErrorMap}
              />
            </div>
            <div className="form-builder-col-properties">
              <FormFieldPropertiesPanel
                section={sections[selectedIndex]}
                field={selectedFieldIndex != null ? sections[selectedIndex]?.fields?.[selectedFieldIndex] : null}
                allFieldKeys={allFieldKeys}
                allFields={allFields}
                onChangeSection={changeSection}
                onChangeField={changeField}
                readOnly={readOnly}
                fieldErrors={selectedFieldErrors}
              />
            </div>
          </div>
        </Tab>

        <Tab eventKey="scoring" title={<TabTitleWithBadge label="Scoring" count={errorsByTab[TABS.SCORING]?.length} />}>
          <ScoringConfigEditor ref={gradeConfigRef} value={scoring} onChange={setScoring} readOnly={readOnly} error={gradeConfigError} />
          {!readOnly && (
            <Button className="unit-btn unit-btn-primary mt-3" onClick={saveScoring} disabled={saving}>
              <i className="fa-solid fa-floppy-disk" aria-hidden="true"></i>
              Save Scoring
            </Button>
          )}
        </Tab>

        <Tab eventKey="execution" title={<TabTitleWithBadge label="Execution Profile" count={errorsByTab[TABS.EXECUTION_PROFILE]?.length} />}>
          <Alert variant="secondary" className="small">
            Configures this version for UAT/Testing-style repeatable test-case execution — result tracking, pass-rate
            calculation and failed-row task creation. Relevant for <code>UAT</code>/<code>TESTING</code> forms, or
            any form where you deliberately want this behavior. Save the Structure tab first so sections/fields have
            real ids before referencing them here.
          </Alert>

          {profileValidation?.length > 0 && (
            <Alert variant="danger">
              <strong>Configuration issues:</strong>
              <ul className="mb-0">{profileValidation.map((e, i) => <li key={i}>{e.message}</li>)}</ul>
            </Alert>
          )}

          <Row className="g-3">
            <Col md={6}>
              <Form.Label className="small">Primary Repeatable Section (test cases)</Form.Label>
              <Form.Select
                size="sm" disabled={readOnly}
                value={executionProfile?.primary_repeatable_section_id || ""}
                onChange={(e) => updateProfile({ primary_repeatable_section_id: Number(e.target.value) || null, result_status_field_id: null })}
              >
                <option value="">Select a repeatable section...</option>
                {repeatableSections.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label className="small">Result Status Field (must be a RESULT_STATUS field in that section)</Form.Label>
              <Form.Select
                size="sm" disabled={readOnly || !executionProfile?.primary_repeatable_section_id}
                value={executionProfile?.result_status_field_id || ""}
                onChange={(e) => updateProfile({ result_status_field_id: Number(e.target.value) || null })}
              >
                <option value="">Select...</option>
                {primarySectionFields.filter((f) => f.field_type === "RESULT_STATUS").map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </Form.Select>
            </Col>

            <Col md={4}>
              <Form.Label className="small">Test Case ID Field (optional)</Form.Label>
              <Form.Select size="sm" disabled={readOnly} value={executionProfile?.test_case_identifier_field_id || ""} onChange={(e) => updateProfile({ test_case_identifier_field_id: Number(e.target.value) || null })}>
                <option value="">None</option>
                {primarySectionFields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="small">Test Case Title Field (optional)</Form.Label>
              <Form.Select size="sm" disabled={readOnly} value={executionProfile?.test_case_title_field_id || ""} onChange={(e) => updateProfile({ test_case_title_field_id: Number(e.target.value) || null })}>
                <option value="">None</option>
                {primarySectionFields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="small">Severity Field (optional)</Form.Label>
              <Form.Select size="sm" disabled={readOnly} value={executionProfile?.severity_field_id || ""} onChange={(e) => updateProfile({ severity_field_id: Number(e.target.value) || null })}>
                <option value="">None</option>
                {primarySectionFields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="small">Actual Result Field (optional)</Form.Label>
              <Form.Select size="sm" disabled={readOnly} value={executionProfile?.actual_result_field_id || ""} onChange={(e) => updateProfile({ actual_result_field_id: Number(e.target.value) || null })}>
                <option value="">None</option>
                {primarySectionFields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="small">Expected Result Field (optional)</Form.Label>
              <Form.Select size="sm" disabled={readOnly} value={executionProfile?.expected_result_field_id || ""} onChange={(e) => updateProfile({ expected_result_field_id: Number(e.target.value) || null })}>
                <option value="">None</option>
                {primarySectionFields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="small">Evidence Field (optional)</Form.Label>
              <Form.Select size="sm" disabled={readOnly} value={executionProfile?.evidence_field_id || ""} onChange={(e) => updateProfile({ evidence_field_id: Number(e.target.value) || null })}>
                <option value="">None</option>
                {primarySectionFields.filter((f) => f.field_type === "FILE_UPLOAD" || f.field_type === "IMAGE_UPLOAD").map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </Form.Select>
            </Col>

            <Col md={3}>
              <Form.Check type="checkbox" label="Require all cases executed" disabled={readOnly} checked={!!executionProfile?.require_all_cases_executed} onChange={(e) => updateProfile({ require_all_cases_executed: e.target.checked })} />
            </Col>
            <Col md={3}>
              <Form.Check type="checkbox" label="Include Blocked in pass-rate denominator" disabled={readOnly} checked={executionProfile?.include_blocked_in_pass_rate_denominator !== false} onChange={(e) => updateProfile({ include_blocked_in_pass_rate_denominator: e.target.checked })} />
            </Col>
            <Col md={3}>
              <Form.Check type="checkbox" label="Exclude N/A from denominator" disabled={readOnly} checked={executionProfile?.exclude_na_from_denominator !== false} onChange={(e) => updateProfile({ exclude_na_from_denominator: e.target.checked })} />
            </Col>
            <Col md={3}>
              <Form.Check type="checkbox" label="Enable task creation from failed rows" disabled={readOnly} checked={!!executionProfile?.task_creation_enabled} onChange={(e) => updateProfile({ task_creation_enabled: e.target.checked })} />
            </Col>
          </Row>

          {!readOnly && (
            <div className="d-flex gap-2 mt-3">
              <Button variant="outline-secondary" onClick={runProfileValidation}>Validate</Button>
              <Button variant="primary" disabled={savingProfile} onClick={saveExecutionProfile}>{savingProfile ? "Saving..." : "Save Execution Profile"}</Button>
            </div>
          )}

          {executionProfile?.id && executionProfile?.task_creation_enabled && (
            <div className="mt-4 border-top pt-3">
              <FormTaskCreationRuleEditor
                formId={formId}
                versionId={versionId}
                resultStatusFieldId={executionProfile.result_status_field_id}
                readOnly={readOnly}
              />
            </div>
          )}
        </Tab>

        <Tab eventKey="preview" title="Preview">
          <FormPreview sections={sections} validation={validation} />
        </Tab>
      </Tabs>

      <ConfirmModal
        show={showPublishConfirm}
        onHide={() => setShowPublishConfirm(false)}
        onConfirm={confirmPublish}
        busy={saving}
        title="Publish Form Version?"
        message={`Publishing Version ${version.version_number} will make it immutable and set it as the active published version for this form.`}
        confirmLabel="Publish Version"
        confirmVariantClass="unit-btn-primary"
        dialogClassName="admin-teal-modal admin-teal-modal-sm"
      />

      <ConfirmModal
        show={showReopenConfirm}
        onHide={() => setShowReopenConfirm(false)}
        onConfirm={reopenCurrentVersion}
        busy={reopening}
        busyLabel="Reopening..."
        title="Edit Current Version?"
        message={
          <>
            No responses have started for this version.
            <br /><br />
            Version {version.version_number} will be moved back to Draft so you can edit it.
            Existing pending assignments will remain linked to this version.
            <br /><br />
            Publish it again when your changes are complete.
          </>
        }
        confirmLabel={`Edit Version ${version.version_number}`}
        confirmVariantClass="unit-btn-primary"
        dialogClassName="admin-teal-modal admin-teal-modal-sm"
      />

      <ConfirmModal
        show={!!pendingFieldDelete}
        onHide={cancelRemoveField}
        onConfirm={confirmRemoveField}
        title={`Delete "${pendingDeleteField?.label || pendingDeleteField?.field_key || "this field"}"?`}
        message={
          pendingDeleteOptionCount > 0
            ? `This field has ${pendingDeleteOptionCount} option${pendingDeleteOptionCount === 1 ? "" : "s"}. Deleting the field will also remove all of its options.`
            : "Delete this field?"
        }
        confirmLabel="Delete Field"
        dialogClassName="admin-teal-modal admin-teal-modal-sm"
      />
    </div>
  );
}
